import { FormField, Input, Select } from '@/shared/components';
import { PropertyAddressAutocomplete } from '@/features/properties/property-form/PropertyAddressAutocomplete';
import type { AddressDto } from '@/shared/api';

export interface NewObjectDraft {
  name: string;
  query: string;
  address: AddressDto;
  needsAddressReview: boolean;
}

export function emptyDraft(): NewObjectDraft {
  return {
    name: '',
    query: '',
    address: {
      street: '',
      streetName: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      countryCode: 'DE',
    },
    needsAddressReview: false,
  };
}

/** Vollständig genug, dass der Server sie annimmt — dieselben Pflichtfelder wie im Objekt-Formular. */
export function draftIsComplete(draft: NewObjectDraft): boolean {
  const { name, address } = draft;
  return (
    name.trim().length >= 2 &&
    address.streetName.trim().length >= 2 &&
    address.houseNumber.trim().length >= 1 &&
    address.postalCode.trim().length >= 2 &&
    address.city.trim().length >= 2 &&
    address.countryCode.trim().length === 2
  );
}

interface Props {
  draft: NewObjectDraft;
  disabled?: boolean;
  onChange: (draft: NewObjectDraft) => void;
}

/**
 * Name und Adresse eines neu entstehenden Objekts.
 *
 * Die Adresse ist Pflicht und darf nicht die des Quellobjekts sein: der Server erkennt Objekte an
 * ihrer normalisierten Adresse und würde ein Duplikat mit `object_split_address_exists` ablehnen.
 * Eine von Hand geänderte Adresse gilt bewusst als ungeprüft.
 */
export function NewObjectFields({ draft, disabled, onChange }: Props) {
  const setAddress = (patch: Partial<AddressDto>, manual: boolean) => {
    const address = { ...draft.address, ...patch };
    onChange({
      ...draft,
      address: {
        ...address,
        street: `${address.streetName.trim()} ${address.houseNumber.trim()}`.trim(),
        ...(manual ? { latitude: null, longitude: null } : {}),
      },
      needsAddressReview: manual ? true : draft.needsAddressReview,
    });
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <FormField label="Name des neuen Objekts" required className="md:col-span-2">
        <Input
          value={draft.name}
          disabled={disabled}
          onChange={(event) => onChange({ ...draft, name: event.target.value })}
        />
      </FormField>
      <FormField label="Adresse suchen" className="md:col-span-2">
        <PropertyAddressAutocomplete
          value={draft.query}
          onChange={(query) => onChange({ ...draft, query })}
          onSelect={(suggestion) =>
            onChange({
              ...draft,
              query: suggestion.label,
              address: { ...suggestion.address, street: suggestion.label },
              needsAddressReview: suggestion.needsAddressReview,
            })
          }
        />
      </FormField>
      <FormField label="Straße" required>
        <Input
          value={draft.address.streetName}
          disabled={disabled}
          onChange={(event) => setAddress({ streetName: event.target.value }, true)}
        />
      </FormField>
      <FormField label="Hausnummer" required>
        <Input
          value={draft.address.houseNumber}
          disabled={disabled}
          onChange={(event) => setAddress({ houseNumber: event.target.value }, true)}
        />
      </FormField>
      <FormField label="PLZ" required>
        <Input
          value={draft.address.postalCode}
          disabled={disabled}
          onChange={(event) => setAddress({ postalCode: event.target.value }, true)}
        />
      </FormField>
      <FormField label="Stadt" required>
        <Input
          value={draft.address.city}
          disabled={disabled}
          onChange={(event) => setAddress({ city: event.target.value }, true)}
        />
      </FormField>
      <FormField label="Land" required className="md:col-span-2">
        <Select
          value={draft.address.countryCode}
          disabled={disabled}
          aria-label="Land des neuen Objekts"
          onChange={(event) => setAddress({ countryCode: event.target.value }, true)}
        >
          <option value="DE">DE — Deutschland</option>
          <option value="AT">AT — Österreich</option>
          <option value="CH">CH — Schweiz</option>
        </Select>
      </FormField>
    </div>
  );
}
